/**
 * Logger utility for frontend
 * Provides structured and configurable logging
 */

// Log levels
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Logger configuration
interface LoggerConfig {
  enableConsoleOutput: boolean;
  minLevel: LogLevel;
  prefixTimestamp: boolean;
  showContext: boolean;
}

// Default configuration based on environment
const defaultConfig: LoggerConfig = {
  enableConsoleOutput: true,
  minLevel: import.meta.env.DEV ? 'debug' : 'warn', // Only show warnings/errors in production
  prefixTimestamp: import.meta.env.DEV,
  showContext: import.meta.env.DEV,
};

// Level priority map for filtering
const levelPriority: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private config: LoggerConfig;
  private context: string;

  constructor(context: string = '', config: Partial<LoggerConfig> = {}) {
    this.context = context;
    this.config = { ...defaultConfig, ...config };
  }

  // Ensure a message meets the minimum log level
  private shouldLog(level: LogLevel): boolean {
    return (
      this.config.enableConsoleOutput &&
      levelPriority[level] >= levelPriority[this.config.minLevel]
    );
  }

  // Format a log message with optional context and timestamp
  private formatMessage(message: string): string {
    const parts: string[] = [];

    if (this.config.prefixTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (this.config.showContext && this.context) {
      parts.push(`[${this.context}]`);
    }

    parts.push(message);
    return parts.join(' ');
  }

  // Log methods for each level
  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage(message), ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(message), ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(message), ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage(message), ...args);
    }
  }

  // Create a child logger with a specific context
  child(childContext: string): Logger {
    const combinedContext = this.context
      ? `${this.context}:${childContext}`
      : childContext;
    return new Logger(combinedContext, this.config);
  }
}

// Create the application-wide logger instance
export const logger = new Logger('App');

// Export specific context loggers for different modules
export const apiLogger = logger.child('API');
export const authLogger = logger.child('Auth');
export const rbacLogger = logger.child('RBAC');
export const uiLogger = logger.child('UI');
